import LinearProgress from '@mui/material/LinearProgress';
import { useQuery } from '@tanstack/react-query';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { fetchHomepageData, fetchLanguage, fetchUserPicture } from '../../api/mobileapi';
import { HomeData } from '../../api/types';
import { Locale, translations } from '../../translations/translations';
import { AdamsError } from '../../utils/errors';
import { GlobalError } from '../GlobalError/GlobalError';

export const getLanguageFromLocalstorage = () => {
    return localStorage.getItem('language');
};

interface HomeDataContextValue {
    currentLanguage: Locale;
    homepageData: HomeData;
    setLanguage: (arg: Locale) => void;
    refetchHomepageData: Function;
    userPicture: string;
}

const HomeDataContext = createContext<HomeDataContextValue | undefined>(undefined);

interface HomeDataProviderProps {
    children: React.ReactNode;
}

export const HomeDataProvider: React.FC<HomeDataProviderProps> = ({ children }) => {
    const getDefaultLanguage = (): Locale => {
        const languageIsFrench = navigator.language.toUpperCase().includes('FR');
        const languageInLocalStorage = getLanguageFromLocalstorage();
        if (languageInLocalStorage && (languageInLocalStorage === 'EN' || languageInLocalStorage === 'FR')) {
            return languageInLocalStorage;
        }
        return languageIsFrench ? 'FR' : 'EN';
    };
    const [currentLanguage, setCurrentLanguage] = useState<Locale>(getDefaultLanguage());

    const { data: language, isLoading: languageLoading } = useQuery(['language'], () => fetchLanguage(), {
        initialData: { preferredLanguage: getDefaultLanguage() },
        enabled: !getLanguageFromLocalstorage(),
        select: (data) => data.preferredLanguage,
    });

    const {
        data: homepageData,
        error: homepageDataError,
        isLoading: homepageDataLoading,
        refetch: refetchHomepageData,
    } = useQuery<HomeData, AdamsError>(['homepageData'], () => fetchHomepageData({ language: currentLanguage }), {
        enabled: Boolean(currentLanguage),
    });

    const {
        data: userPicture,
        error: userPictureError,
        isLoading: userPictureLoading,
    } = useQuery<string | undefined, AdamsError>(
        ['userPicture', homepageData?.personID],
        () => fetchUserPicture(homepageData!),
        {
            enabled: !!homepageData,
        }
    );

    const setLanguage = (lang: Locale) => {
        setCurrentLanguage(lang);
        localStorage.setItem('language', lang);
    };

    useEffect(() => {
        if (language) {
            setLanguage(language);
        }
    }, [language]);

    useEffect(() => {
        if (currentLanguage) {
            refetchHomepageData();
        }
    }, [currentLanguage]);

    const error = homepageDataError || userPictureError;
    if (error) {
        return <GlobalError error={error} translations={translations[currentLanguage]} />;
    }

    if (languageLoading || homepageDataLoading || userPictureLoading || !homepageData || !userPicture)
        return <LinearProgress />;

    return (
        <HomeDataContext.Provider
            value={{
                currentLanguage,
                homepageData,
                setLanguage,
                refetchHomepageData,
                userPicture,
            }}
        >
            <>{children}</>
        </HomeDataContext.Provider>
    );
};

export const useHomeData = () => {
    const value = useContext(HomeDataContext);
    if (value === undefined) {
        throw new Error();
    }
    return value;
};
